import type {
  CatalogProvider,
  PerkGrantPolicyKind,
  PerkType,
  ProductPerkOverduePolicy,
  ProductStatus,
} from '../domain/entities/catalog.entity';

/** Rótulos amigáveis (pt-BR) para os enums do catálogo. */

export const CATALOG_PROVIDER_LABELS: Record<CatalogProvider, string> = {
  guru: 'Guru',
  hotmart: 'Hotmart',
};

export const PRODUCT_STATUS_LABELS: Record<ProductStatus, string> = {
  missing_config: 'Sem configuração',
  configured: 'Configurado',
  archived: 'Arquivado',
};

export const PRODUCT_STATUS_BADGE: Record<
  ProductStatus,
  'default' | 'success' | 'warning' | 'error' | 'info'
> = {
  missing_config: 'warning',
  configured: 'success',
  archived: 'default',
};

export const PERK_TYPE_LABELS: Record<PerkType, string> = {
  role: 'Role',
  feature: 'Feature',
};

/** Rótulo tolerante a tipo vindo como string crua (ex.: `perkType` do grant). */
export function perkTypeLabel(type: string): string {
  return (
    PERK_TYPE_LABELS[type.toLowerCase() as PerkType] ??
    PERK_TYPE_LABELS[type as PerkType] ??
    type
  );
}

export const OVERDUE_POLICY_LABELS: Record<ProductPerkOverduePolicy, string> = {
  keep_active: 'Manter ativo',
  suspend: 'Suspender',
};

/**
 * `ProductPerk.deliversPerCycle` na tela. O rótulo fala do **produto**, não da
 * cobrança: a Primum cobra parcela e renovação igual, e o que muda é se o
 * ciclo pago reentrega o perk ou não.
 */
export const DELIVERS_PER_CYCLE_LABELS: Record<'true' | 'false', string> = {
  false: 'Entrega uma vez',
  true: 'Reentrega a cada ciclo',
};

export const GRANT_POLICY_LABELS: Record<PerkGrantPolicyKind, string> = {
  time_limited_credit: 'Crédito por tempo limitado',
  usage_limited_credit: 'Crédito por uso limitado',
  time_bounded_recurring_credit: 'Crédito recorrente com prazo',
  time_bounded_unlimited_usage: 'Uso ilimitado com prazo',
  permanent: 'Permanente',
};

/** Rótulo tolerante a kind vindo como string crua (ex.: grant já concedido). */
export function grantPolicyLabel(kind: string): string {
  return GRANT_POLICY_LABELS[kind as PerkGrantPolicyKind] ?? kind;
}

const MS_PER_DAY = 86_400_000;

/** Converte dias → milissegundos (campos `*InMilliseconds` do back). */
export function daysToMs(days: number): number {
  return Math.round(days * MS_PER_DAY);
}

/** Converte milissegundos → dias (para exibição). */
export function msToDays(ms: number): number {
  return Math.round(ms / MS_PER_DAY);
}

/**
 * Os valores de uma concessão em uma linha ("30 dias · 10 usos · renova a cada
 * 7 dias"). Serve item de preset e associação a produto — os dois carregam os
 * mesmos três campos.
 */
export function describeGrantPolicyValues(values: {
  durationInMilliseconds?: number | null;
  maxUsages?: number | null;
  renewEveryInMilliseconds?: number | null;
}): string {
  const parts: string[] = [];
  if (values.durationInMilliseconds != null) {
    const days = msToDays(values.durationInMilliseconds);
    parts.push(`${days} ${days === 1 ? 'dia' : 'dias'}`);
  }
  if (values.maxUsages != null) {
    parts.push(
      `${values.maxUsages} ${values.maxUsages === 1 ? 'uso' : 'usos'}`,
    );
  }
  if (values.renewEveryInMilliseconds != null) {
    const days = msToDays(values.renewEveryInMilliseconds);
    parts.push(`renova a cada ${days} ${days === 1 ? 'dia' : 'dias'}`);
  }
  return parts.length > 0 ? parts.join(' · ') : 'Sem valores';
}
